// This file contains map layers that are shared between multiple tenants.

import { environment } from '../../../../../environments/environment';
import { ILayer } from '../layer.type';
import { _ } from '../../../utils';
import { cloneDeep } from 'lodash-es';

export const SYKE_ATTRIBUTION =
  '<a href="https://www.syke.fi/avointieto" target="_blank">SYKE</a>';

export function generateDigitrafficTimeParam(): string {
  const now = new Date();
  const year = now.getUTCFullYear().toString();
  const month = (now.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = now.getUTCDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}T00:00:00Z/${year}-${month}-${day}T00:00:00Z`;
}

const timeParam = encodeURIComponent(generateDigitrafficTimeParam());

const layers: {[key: string]: ILayer} = {
  levelcrossings: {
    id: 'Tasoristeykset',
    name: _('Train level crossing'),
    geojsonURL: 'https://rata.digitraffic.fi/infra-api/0.2/tasoristeykset.geojson?srsName=crs%3A84&time=' + timeParam,
    selectable: true,
    style: {
      layerType: 'symbol',
      sourceType: 'geojson',
      clickable: true,
      icon: { url: environment.assetRoot + '/data/vr/tasoristeys.png' },
      visible: false
    },
    metadata: {
      group: _('Rail infrastructure'),
      component: 'ElementDetailsComponent',
      hoverComponent: 'ElementDetailsHover',
      hoverProperties: {
        coordinates: false,
        properties: ['nimi', 'tunnus']
      },
      visibleByZoom: 10,
      uniqueBy: 'tunnus',
      labelBy: 'nimi',
    },
    searchOptions: {
      minimumSearchTermLength: 1,
      interestingProperties: [
        { key: 'nimi', type: 'contains' },
        { key: 'tunnus', type: 'contains' }
      ],
      searchable: false
    },
    useWorker: true,
    orderingWeight: 0
  },
  rails: {
    id: 'Raiteet',
    name: _('Rails'),
    geojsonURL: 'https://rata.digitraffic.fi/infra-api/0.2/radat.geojson?srsName=crs%3A84&time=' + timeParam,
    selectable: true,
    belowRoadLayers: true,
    style: {
      layerType: 'line',
      sourceType: 'geojson',
      clickable: true,
      strokeColor: '#5c5c60',
      strokeWeight: 1.5,
      visible: true
    },
    metadata: {
      group: _('Rail infrastructure'),
      component: 'ElementDetailsComponent',
      hoverComponent: 'ElementDetailsHover',
      hoverProperties: {
        coordinates: false,
        properties: []
      },
      uniqueBy: 'tunniste',
      labelBy: 'ratanumero',
    },
    searchOptions: {
      minimumSearchTermLength: 1,
      interestingProperties: [],
      searchable: false
    },
    useWorker: true,
    orderingWeight: 0
  },
  trafficControlPoints: {
    id: 'Liikennepaikat',
    name: _('Traffic control points'),
    geojsonURL: 'https://rata.digitraffic.fi/infra-api/0.2/rautatieliikennepaikat.geojson?srsName=crs%3A84&time=' + timeParam,
    filter: {
      mode: 'AND',
      filters: [
        {
          field: 'lyhenne',
          value: ['Hki', 'Psl', 'Tpe', 'Kv', 'Sk', 'Ol', 'Tkl', 'Lh', 'Ri', 'Pm', 'Tku', 'Jns', 'Jy', 'Yv', 'Ilm'],
          mode: 'exclusive' // Eli hyväksytään arvot, joita ei löydy value -arraysta
        },
        {
          field: 'uicKoodi',
          value: ['0'],
          mode: 'exclusive'
        },
        {
          field: 'nimi',
          value: [''],
          mode: 'exclusive'
        }
      ]
    },
    orderingWeight: -2,
    mapboxOptions: {
      paint: {
        'text-color': 'black',
        'text-halo-color': 'hsl(35, 8%, 85%)',
        'text-halo-width': 0.8,
      },
      layout: {
        'icon-image': 'circle_gray',
        'icon-size': 0.85,
        'icon-offset': [-15, 0],
        'icon-allow-overlap': true,
        'text-field': ['get', 'nimi'],
        'text-font': ['Arial Unicode MS Regular'],
        'text-max-width': 7,
        'text-padding': 4,
        'text-size': 11,
        'text-offset': [-1.0, 0.0],
        'text-optional': true,
        //'text-allow-overlap': true
      },
    },
    metadata: {
      group: _('Rail infrastructure'),
      uniqueBy: 'nimi',
      labelBy: 'nimi',
      visibleByZoom: 8,
      component: 'StationInfoComponent'
    },
    searchOptions: {
      minimumSearchTermLength: 1,
      priority: -10,
      interestingProperties: [
        { key: 'nimi', type: 'contains' },
        { key: 'lyhenne', type: 'contains' }
      ],
      searchable: true,
    },
    style: {
      sourceType: 'geojson',
      layerType: 'symbol',
      visible: true,
      icon: {
        url: environment.assetRoot + '/img/rego/circle-gray.png',
      },
    },
    useWorker: true,
  },
  intersectionStations: {
    id: 'Risteysasemat',
    name: _('Intersection stations'),
    selectable: true,
    geojsonURL: 'https://rata.digitraffic.fi/infra-api/0.2/rautatieliikennepaikat.geojson?srsName=crs%3A84&time=' + timeParam,
    filter: {
      filters: [
        {
          field: 'lyhenne',
          value: ['Hki', 'Psl', 'Tpe', 'Kv', 'Sk', 'Ol', 'Tkl', 'Lh', 'Ri', 'PM', 'Tku', 'Jns', 'Jy', 'Yv', 'Ilm'],
          mode: 'inclusive' // Eli hyväksytään arvot, jotka löytyy value -arraysta
        }
      ]
    },
    metadata: {
      group: _('Rail infrastructure'),
      component: 'StationInfoComponent',
      hoverComponent: 'ElementDetailsHover',
      hoverProperties: {
        coordinates: false,
        properties: ['nimi', 'lyhenne']
      },
      uniqueBy: 'nimi',
      labelBy: 'nimi'
    },
    searchOptions: {
      minimumSearchTermLength: 1,
      priority: 1,
      interestingProperties: [
        { key: 'nimi', type: 'contains' },
        { key: 'lyhenne', type: 'contains' }
      ],
      searchable: true,
    },
    style: {
      layerType: 'symbol',
      sourceType: 'geojson',
      clickable: true,
      visible: true,
      icon: { url: environment.assetRoot + '/data/vr/risteysasema.png' },
      scale: 3
    },
    useWorker: true,
    orderingWeight: -1010
  },
  flags: {
    id: 'flagLayer',
    name: 'flagLayer',
    orderingWeight: -5,
    mapboxOptions: {
      layout: {
        'icon-offset': [15, -15]
      }
    },
    style: {
      visible: true,
      layerType: 'symbol',
      sourceType: 'geojson',
      icon: {
        url: environment.assetRoot + '/img/flag-icon.png'
      }
    },
    metadata: {
      labelBy: 'label',
      uniqueBy: 'id',
      userTogglable: false,
      hoverComponent: 'ElementDetailsHover',
      hoverProperties: {
        coordinates: false,
        properties: []
      },
    },
    searchOptions: { searchable: false }
  },
  elevationContour: {
    id: 'Korkeuskäyrät',
    name: _('Elevation'),
    orderingWeight: 0,
    vectorURL: 'mapbox://mapbox.mapbox-terrain-v2',
    vectorSourceLayer: 'contour',
    belowRoadLayers: true,
    selectable: true,
    style: {
      layerType: 'line',
      sourceType: 'vector',
      clickable: true,
      strokeColor: 'rgba(0, 0, 0, 0.4)',
      visible: false,
    },
    metadata: {
      uniqueBy: 'id',
      labelBy: 'id',
      group: _('Contour lines'),
      visibleByZoom: 11,
      hoverComponent: 'ElementDetailsHover',
      hoverProperties: { coordinates: false, properties: ['ele'] },
    },
    searchOptions: { searchable: false },
    useWorker: true,
  },
  depthContour: {
    id: 'Syvyyskäyrät',
    name: _('Depth'),
    orderingWeight: 0,
    vectorURL: 'mapbox://mipro-dev.37vi3c90',
    vectorSourceLayer: 'syvyyskayra-34x32i',
    belowRoadLayers: true,
    selectable: true,
    style: {
      layerType: 'line',
      sourceType: 'vector',
      clickable: true,
      strokeColor: 'rgba(255, 255, 255, 0.4)',
      visible: false,
    },
    metadata: {
      attribution:
        SYKE_ATTRIBUTION,
      uniqueBy: 'id',
      labelBy: 'id',
      group: _('Contour lines'),
      visibleByZoom: 8,
      hoverComponent: 'ElementDetailsHover',
      hoverProperties: { coordinates: false, properties: ['Syvyyskayr'] },
    },
    searchOptions: { searchable: false },
    useWorker: true,
  },
  groundWaterAreas: {
    id: 'Pohjavesialueet',
    name: _('Ground water areas'),
    orderingWeight: 1,
    vectorURL: 'mapbox://mipro-dev.47onzc66',
    vectorSourceLayer: 'pohjavesialueet-5mmi4e',
    belowRoadLayers: true,
    selectable: true,
    style: {
      layerType: 'fill',
      sourceType: 'vector',
      clickable: true,
      fillColor: 'rgba(52, 149, 235, 0.4)',
      visible: false,
    },
    metadata: {
      attribution:
        SYKE_ATTRIBUTION,
      uniqueBy: 'PvAlueTunn',
      labelBy: 'PvAlueNimi',
      group: _('Water areas'),
      visibleByZoom: 6,
      hoverComponent: 'ElementDetailsHover',
      hoverProperties: {
        coordinates: false,
        properties: [
          'AntoisuusA',
          'DigOrg',
          'DigPohja',
          'KuntaNimi',
          'MuodAlueTu',
          'MuutosPvm',
          'PAlaMuodAl',
          'PAlaPvAlue',
          'Po_Pohjave',
          'PvAlueLuok',
          'PvAlueNimi',
          'PvAlueTunn',
          'PvTunnusLo',
          'RiskiArvio',
          'Subtype',
          'SuojSuunn',
          'TilaKemia',
          'TilaMaara',
          'VHATunnus',
          'Yht_Yha',
        ],
      },
    },
    searchOptions: { searchable: false },
    useWorker: true,
  },
  drainageAreas: {
    id: 'Valuma-alueet',
    name: _('Drainage basin areas'),
    orderingWeight: 1,
    vectorURL: 'mapbox://mipro-dev.88c216ny',
    vectorSourceLayer: 'valuma-carx2l',
    belowRoadLayers: true,
    selectable: true,
    style: {
      layerType: 'line',
      sourceType: 'vector',
      clickable: true,
      strokeColor: 'rgba(113, 44, 209, 0.4)',
      strokeWeight: 1,
      visible: false,
    },
    metadata: {
      attribution:
        SYKE_ATTRIBUTION,
      uniqueBy: 'id',
      labelBy: 'id',
      group: _('Water areas'),
      visibleByZoom: 6,
      hoverComponent: 'ElementDetailsHover',
      hoverProperties: {
        coordinates: false,
        properties: ['MuutosPvm', 'Tyyppi'],
      },
    },
    searchOptions: { searchable: false },
    useWorker: true,
  },
  basinOutlets: {
    id: 'Purkupisteet',
    name: _('Basin outlets'),
    orderingWeight: 0,
    vectorURL: 'mapbox://mipro-dev.bhr1t5bf',
    vectorSourceLayer: 'valumapurku-5iyi7y',
    belowRoadLayers: true,
    selectable: true,
    style: {
      layerType: 'symbol',
      sourceType: 'vector',
      clickable: true,
      icon: {
        // the icon used here must point west before any rotation
        url: environment.assetRoot + '/data/T8/purkupiste.png',
        // documentation says: SuuntaP virtaussuunta, kulma asteina (0 360), 0 = länteen ja kiertosuunta vastapäivään
        rotation: ['-', ['get', 'SuuntaP']],
        opacity: 0.6,
      },
      visible: false,
    },
    metadata: {
      attribution:
        SYKE_ATTRIBUTION,
      uniqueBy: 'PurkuTunnu',
      labelBy: 'PurkuTunnu',
      group: _('Water areas'),
      hoverComponent: 'ElementDetailsHover',
      hoverProperties: {
        coordinates: false,
        properties: [
          'PurkuNimi',
          'PurkuTaso',
          'PurkuTunnu',
          'SuuntaP',
          'ValumaTunn',
        ],
      },
    },
    searchOptions: { searchable: false },
    useWorker: true,
  },
  houseNumbers: {
    id: 'House numbers',
    name: _('House numbers'),
    vectorURL: 'mapbox://mapbox.mapbox-streets-v8',
    vectorSourceLayer: 'housenum_label',
    orderingWeight: -1,
    mapboxOptions: {
      paint: {
        'text-color': 'black',
        'text-halo-color': 'hsl(35, 8%, 85%)',
        'text-halo-width': 0.5,
      },
      layout: {
        'icon-image': 'house_number_plate',
        'icon-size': 0.85,
        'text-field': ['get', 'house_num'],
        'text-font': ['DIN Offc Pro Italic', 'Arial Unicode MS Regular'],
        'text-max-width': 7,
        'text-padding': 4,
        'text-size': 11,
      },
    },
    metadata: {
      labelBy: 'id',
      uniqueBy: 'id',
      visibleByZoom: 17,
    },
    searchOptions: { searchable: false },
    style: {
      layerType: 'symbol',
      sourceType: 'vector',
      visible: true,
      icon: {
        url: environment.assetRoot + '/img/house_number_plate.png',
      },
    },
  },
};

export function getSharedLayer(key: keyof typeof layers): ILayer {
  return cloneDeep(layers[key]);
}
